import { injectable } from 'inversify';
import container from '@core/di';
import BaseStore from '@core/stores/base';
import { makeObservable, observable, runInAction, action } from 'mobx';
import {
  Intervention,
  InterventionResourceUpdate,
  InterventionResourceOrderUpdate,
} from '@shared/models/interventions';
import InterventionsService from '@shared/services/interventions';
import {
  InterventionContent,
  InterventionUploadResourceData,
} from '@shared/models/interventioncontent';

@injectable()
export default class InterventionsStore extends BaseStore<Intervention, InterventionsService> {
  static diToken = Symbol('interventions-store');
  private _therapeuticInterventions: Intervention[] = [];
  private _interventionContent: InterventionContent[] = [];
  private _treatmentOptionContent: { number: InterventionContent[] } = { number: [] };

  constructor() {
    super({ service: container.get<InterventionsService>(InterventionsService.diToken) });
    makeObservable<any>(this, {
      _therapeuticInterventions: observable,
      _interventionContent: observable,
      _treatmentOptionContent: observable,
      updateInterventionContentById: action,
      removeResourceByInterventionId: action,
      addResourceByInterventionId: action,
    });

    this.loading = {
      list: false,
      item: false,
    };
  }

  fetchTherapeuticInterventions = async () => {
    const therapeuticInterventions = await this.service.getTerapeuticInterventions();

    runInAction(() => {
      this._therapeuticInterventions = therapeuticInterventions;
    });

    return therapeuticInterventions;
  };

  get therapeuticInterventions(): Intervention[] {
    return this._therapeuticInterventions;
  }

  get treatmentOptionContent(): { number: InterventionContent[] } {
    return this._treatmentOptionContent;
  }

  get interventionContent(): InterventionContent[] {
    return this._interventionContent;
  }

  get therapeuticInterventionsMenuItems() {
    if (this._therapeuticInterventions) {
      return this._therapeuticInterventions.map(({ id, name }) => ({
        id,
        title: name,
      }));
    }

    return [];
  }

  getTreatmentOptionContentById = async (interventionId: number, treatmentOptionId: number) => {
    const treatmentOptionContent = await this.service.getTreatmentOptionContentById(
      interventionId,
      treatmentOptionId,
    );

    runInAction(() => {
      this._treatmentOptionContent = {
        ...this._treatmentOptionContent,
        [treatmentOptionId]: treatmentOptionContent,
      };
    });

    return treatmentOptionContent;
  };

  getInterventionContentById = async (interventionId: number) => {
    const interventionContent = await this.service.getInterventionContentById(interventionId);

    runInAction(() => {
      this._interventionContent = interventionContent;
    });

    return interventionContent;
  };

  updateInterventionContentById = (
    contentResourceId: number,
    { title, url, treatmentOptionIds }: InterventionResourceUpdate,
  ) => {
    this._interventionContent = this._interventionContent.map(content => {
      if (content.contentResourceId === contentResourceId) {
        return {
          ...content,
          ...(title ? { title } : {}),
          ...(url ? { linkMetadata: { url } } : {}),
          ...(treatmentOptionIds
            ? {
                treatmentOptions: content.treatmentOptions.filter(opt =>
                  treatmentOptionIds.includes(opt.id),
                ),
              }
            : {}),
        } as InterventionContent;
      }

      return content;
    });
  };

  removeResource = async (
    interventionId: number,
    treatmentOptionId: number,
    resourceId: number,
  ) => {
    return await this.service.removeResource(interventionId, treatmentOptionId, resourceId);
  };

  removeResourceByInterventionId = async (interventionId: number, resourceId: number) => {
    await this.service.removeResourceByInterventionId(interventionId, resourceId);

    runInAction(() => {
      this._interventionContent = this._interventionContent.filter(
        content => content.contentResourceId !== resourceId,
      );
    });
  };

  addResourceByTreatmentOption = async (
    interventionId: number,
    treatmentOptionId: number,
    data: InterventionUploadResourceData,
  ) => {
    return this.service.addResourceByTreatmentOption(interventionId, treatmentOptionId, data);
  };

  addResourceByInterventionId = async (
    interventionId: number,
    data: InterventionUploadResourceData,
  ) => {
    const response = await this.service.addResourceByInterventionId(interventionId, data);

    runInAction(() => {
      this._interventionContent = [...this.interventionContent, response.data];
    });
  };

  updateResource = async (
    interventionId: number,
    contentResourceId: number,
    data: InterventionResourceUpdate,
  ) => {
    return this.service.updateResource(interventionId, contentResourceId, data);
  };

  updateResourceOrder = async (
    interventionId: number,
    treatmentOptionId: number,
    data: InterventionResourceOrderUpdate[],
  ) => {
    return this.service.updateResourceOrder(interventionId, treatmentOptionId, data);
  };
}
